import {
  BERRY_100,
  CACTUS_100,
  FOG_100,
  MANGO_100,
  PINE_100
} from '@rategravity/own-up-component-library';
import SliderComponent from 'rc-slider';
import React, { useCallback } from 'react';
import { useCalculator } from '../data/dataProvider';
import { AffordabilityDataActionsTypes } from '../types';
import { handleStyle, Label, railStyle, trackStyle, WrapperWithOverrides } from './elements';

// only display stretching label if there's enough room for it
const computeLabel = (stretchPoint: number, maxAffordable: number) => {
  const lowerBoundary = 25;
  const upperBoundary = 75;

  // Use this for all screen-sizes, so we don't have to rely on a hook to update the style after SSG.
  const mobileOffset = 13;

  if (stretchPoint < lowerBoundary + mobileOffset || stretchPoint > upperBoundary - mobileOffset) {
    return null;
  } else {
    return { [maxAffordable]: <Label>Stretching</Label> };
  }
};

export interface SliderProps {
  // points at which the color gradient shifts
  sliderGradient: {
    // point at which gradient begins shift to yellow (end of affordable / beginning of stretch)
    maxAffordable: number;
    // point at which gradient begins shift to red (end of stretch / beginning of aggressive)
    maxStretch: number;
  };
  // current value of slider
  sliderValue: number;
  // disabled state - slider won't be able to be interacted with if disabled
  disabled: boolean;
  updateSliderValue: (sliderValue: number) => void;
}

// rc-slider default behavior is for the rail to be full width while the track's width adjusts as the handle moves
// we need the opposite behavior in order for the color gradient image to maintain a full width
// this implementation creates that needed effect of the rail overlapping the track
// (combined with forcing the track width to be 100% in StyleOverride)
export const SliderImpl = React.memo(
  ({
    sliderGradient: { maxAffordable, maxStretch },
    sliderValue,
    disabled,
    updateSliderValue
  }: SliderProps) => {
    const maxAffordablePercent = maxAffordable * 100;
    const maxStretchPercent = maxStretch * 100;
    const stretchPoint = (maxStretch + maxAffordable) / 2;
    const stretchingLabelPoint = (maxStretchPercent + maxAffordablePercent) / 2;

    const handleSliderChange = (newSliderValue: number) => {
      updateSliderValue(newSliderValue);
    };

    return (
      <WrapperWithOverrides>
        <SliderComponent
          disabled={disabled}
          value={sliderValue}
          step={0.02}
          min={0}
          max={1}
          marks={{
            [-1]: <Label>{maxStretch <= 0 ? 'Stretching' : 'Affordable'}</Label>,
            ...(disabled ? null : computeLabel(stretchingLabelPoint, stretchPoint)),
            [2]: <Label>{maxAffordable >= 0.86 ? 'Stretching' : 'Aggressive'}</Label>
          }}
          handleStyle={handleStyle}
          trackStyle={{
            ...trackStyle,
            background: `linear-gradient(0.25turn, ${PINE_100}, ${CACTUS_100} ${maxAffordablePercent}%, ${MANGO_100}, ${MANGO_100}, ${BERRY_100} ${maxStretchPercent}%)`
          }}
          railStyle={{
            ...railStyle,
            background: `linear-gradient(to right, transparent, transparent ${
              sliderValue * 100
            }%, ${FOG_100} ${sliderValue * 100}%)`
          }}
          ariaLabelForHandle="affordability slider"
          prefixCls="override"
          onChange={(v) => {
            handleSliderChange(Number(v));
          }}
        />
      </WrapperWithOverrides>
    );
  }
);

SliderImpl.displayName = 'SliderComponent';

export const Slider = ({ disabled = false }: { disabled?: boolean }) => {
  const {
    state: { sliderValue },
    getSliderGradient,
    dispatch
  } = useCalculator();
  const sliderGradient = getSliderGradient();
  const updateSliderValue = useCallback(
    (value: number) =>
      dispatch({ type: AffordabilityDataActionsTypes.SET_SLIDER_VALUE, payload: value }),
    [dispatch]
  );
  return (
    <SliderImpl
      sliderGradient={sliderGradient}
      sliderValue={sliderValue}
      updateSliderValue={updateSliderValue}
      disabled={disabled}
    />
  );
};
